import { useMutation, useQuery } from '@apollo/client';

import { tableSortingMethods } from '../../../../../utils';

import QueryErrorBoundary from '../../../../../components/error-handling';
import { Footer, TitleBar } from '../../../../../components';
import FinancialData from '../../../../../components/common/FinancialData';
import PercentageFormatter from '../../../../../components/common/PercentageFormatter';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../../../components/generic/Table/TableV3';
import { TableColumnsType } from 'antd';
import { UPDATE_IT_APPLICATION } from '../../../../dataInputs/queries/itApplications';
import { useCallback } from 'react';
import TitleUpdater from '../../../../../components/common/TitleUpdater';
import { GET_IT_APPS_SUMMARY } from './queries';
import { CollapsibleText } from '../../../../../components/generic/TextTruncation';

type ITApplicationSummary = {
  __typename: 'ITApplicationSummary';
  applicationDescription: string | null;
  name: string;
  percentageOfCompanySupported: number;
  revenueSupportedAnnualy: number;
};

type ITAppSummaryFetchedType = {
  getItApplicationsSummary: ITApplicationSummary[];
};

const EnterpriseApplicationsSummary = () => {
  const { data, loading, error } =
    useQuery<ITAppSummaryFetchedType>(GET_IT_APPS_SUMMARY);

  const ITAppsSummary = data?.getItApplicationsSummary ?? [];

  const [updateITApplication] = useMutation(UPDATE_IT_APPLICATION, {
    refetchQueries: ['FindAllItApplications', 'GetItApplicationsSummary'],
  });

  const columns: TableColumnsType<ITApplicationSummary> = [
    {
      title: 'Applications',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <div className="flex flex-col">
          <span className="custom-table--column ml-5">{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div>
          Revenue Supported <br />
          Annually
        </div>
      ),
      dataIndex: 'revenueSupportedAnnualy',
      key: 'revenueSupportedAnnualy',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.revenueSupportedAnnualy,
          b.revenueSupportedAnnualy,
        ),
      render: (text: number) => (
        <span className="custom-table--column">
          <FinancialData text={text} />
        </span>
      ),
    },
    {
      title: (
        <div>
          % of Company <br /> Supported
        </div>
      ),
      dataIndex: 'percentageOfCompanySupported',
      key: 'percentageOfCompanySupported',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.percentageOfCompanySupported,
          b.percentageOfCompanySupported,
        ),
      render: (text: number) => (
        <span className="custom-table--column">
          {<PercentageFormatter value={text} />}
        </span>
      ),
    },
    {
      title: 'Application Description',
      dataIndex: 'applicationDescription',
      key: 'applicationDescription',
      width: 200,
      align: 'left',
      render: (text: string) => <CollapsibleText text={text} />,
    },
  ];

  const formFields = [
    {
      label: 'Application Description',
      key: 'applicationDescription',
      type: 'textArea',
    },
  ];

  const itApplicationsSummaryFormatter = useCallback(
    (data: ITApplicationSummary) => {
      return {
        description: data.applicationDescription,
      };
    },
    [],
  );

  const modifyVariables = useCallback(
    ({ values }: ModifyVariablesArgs) => {
      return {
        ...itApplicationsSummaryFormatter(values),
      };
    },
    [itApplicationsSummaryFormatter],
  );

  return (
    <>
      <TitleUpdater title="Enterprise Application Revenue Mapping" />
      <TitleBar
        title="Enterprise Application Revenue Mapping"
        subtitle={`Summary`}
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          columns={columns}
          data={ITAppsSummary}
          loading={loading}
          updateGQL={updateITApplication}
          editable
          editableOptions={{
            update: true,
          }}
          inlineEditing
          formFields={formFields}
          modifyVariables={modifyVariables}
        />
      </QueryErrorBoundary>
      <Footer backPath={`/cyber/enterprise-level/it-applications/mapping`} />
    </>
  );
};

export default EnterpriseApplicationsSummary;
